import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Auth from '@/pages/Auth';
import DashboardHome from '@/pages/DashboardHome';
import DocumentsPage from '@/pages/DocumentsPage';
import DeletedDocumentsPage from '@/pages/DeletedDocumentsPage';
import UploadPage from '@/pages/UploadPage';
import FetchDocsPage from '@/pages/FetchDocsPage';
import RequestsPage from '@/pages/RequestsPage';
import SharesPage from '@/pages/SharesPage';
import EvidencePage from '@/pages/EvidencePage';
import AlertsPage from '@/pages/AlertsPage';
import Viewer from '@/pages/Viewer';
import ViewerAuth from '@/pages/ViewerAuth';
import EnhancedViewer from '@/pages/EnhancedViewer';
import EnhancedViewerComplete from '@/pages/EnhancedViewerComplete';
import AccessRequest from '@/pages/AccessRequest';
import MessagingPage from '@/pages/MessagingPage';
import SharesDebugPage from '@/pages/SharesDebugPage';
import VerifyWatermark from '@/pages/VerifyWatermark';
import OwnerViewer from '@/pages/OwnerViewer';
import NotFound from '@/pages/NotFound';
import { Toaster } from '@/components/ui/sonner';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';

function App() {
  const [token, setToken] = useState(() => localStorage.getItem('udt_token'));
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('udt_user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const handleLogin = (userData, authToken) => {
    localStorage.setItem('udt_token', authToken);
    localStorage.setItem('udt_user', JSON.stringify(userData));
    setToken(authToken);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('udt_token');
    localStorage.removeItem('udt_user');
    setToken(null);
    setUser(null);
  };

  function ProtectedRoute({ children }) {
    const authToken = localStorage.getItem('udt_token');
    if (!authToken) {
      return <Navigate to="/login" replace />;
    }
    return children;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Auth onLogin={handleLogin} />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardHome user={user} token={token} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/documents"
            element={
              <ProtectedRoute>
                <DocumentsPage token={token} user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/deleted-documents"
            element={
              <ProtectedRoute>
                <DeletedDocumentsPage token={token} user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/upload"
            element={
              <ProtectedRoute>
                <UploadPage token={token} user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/fetch-docs"
            element={
              <ProtectedRoute>
                <FetchDocsPage token={token} user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/requests"
            element={
              <ProtectedRoute>
                <RequestsPage token={token} user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/shares"
            element={
              <ProtectedRoute>
                <SharesPage token={token} user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/shares-debug"
            element={
              <ProtectedRoute>
                <SharesDebugPage token={token} user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/evidence"
            element={
              <ProtectedRoute>
                <EvidencePage token={token} user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/alerts"
            element={
              <ProtectedRoute>
                <AlertsPage token={token} user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/messages"
            element={
              <ProtectedRoute>
                <MessagingPage token={token} user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/verify-watermark"
            element={
              <ProtectedRoute>
                <VerifyWatermark token={token} user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/documents/view/:udtId"
            element={
              <ProtectedRoute>
                <OwnerViewer token={token} user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route path="/viewer/:udtId" element={<EnhancedViewer />} />
          <Route path="/viewer-auth/:udtId" element={<ViewerAuth />} />
          <Route path="/request/:udtId" element={<AccessRequest />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
      <PWAInstallPrompt />
    </div>
  );
}

export default App;
